import FirmwareBinParser from '../../utils/FirmwareBinParser';
import { getFirmwareDisplayLabel } from './firmwareCatalog';

const FIRMWARE_NAME_PATTERN =
  /^[^/]*deviceMcu([12])(\d{8})ID([0-9a-f]{8})V([0-9a-f]{8})\.bin$/i;

const firmwareDiscoveryCache = new WeakMap();

export function parseFirmwareFileName(fileName) {
  const match = String(fileName || '').match(FIRMWARE_NAME_PATTERN);
  if (!match) return null;

  return {
    fileName,
    mcuNumber: Number(match[1]),
    date: Number(match[2]),
    dateText: match[2],
    productId: Number.parseInt(match[3], 16),
    version: Number.parseInt(match[4], 16),
  };
}

export function selectLatestFirmwareMetadata(entries, mcuNumber) {
  return (Array.isArray(entries) ? entries : [])
    .map((entry) => {
      const name = typeof entry === 'string' ? entry : entry?.name;
      const metadata = parseFirmwareFileName(name);
      return metadata?.mcuNumber === Number(mcuNumber) ? metadata : null;
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (right.date !== left.date) return right.date - left.date;
      if (right.version !== left.version) return right.version - left.version;
      return right.fileName.localeCompare(left.fileName);
    })[0] || null;
}

export function selectLatestFirmwareFile(entries, mcuNumber) {
  return selectLatestFirmwareMetadata(entries, mcuNumber)?.fileName || null;
}

async function discoverExternalFirmware(file, fetchImpl) {
  const response = await fetchImpl(file.directoryUrl, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`目录清单 HTTP ${response.status}`);
  }

  const fileName = selectLatestFirmwareFile(
    await response.json(),
    file.mcuNumber
  );
  if (!fileName) {
    throw new Error(`目录中没有符合命名格式的 MCU${file.mcuNumber} BIN`);
  }

  return {
    url: `${file.directoryUrl}${encodeURIComponent(fileName)}`,
    fileName,
  };
}

function createFirmwareSelection(file, metadata, details) {
  if (!metadata) {
    throw new Error(
      `${getFirmwareDisplayLabel(file)} 固件文件名不符合版本命名格式`
    );
  }
  if (metadata.mcuNumber !== Number(file.mcuNumber)) {
    throw new Error(
      `${getFirmwareDisplayLabel(file)} 固件文件名中的 MCU 类型不匹配`
    );
  }
  if (
    file.expectedProductId != null &&
    metadata.productId !== file.expectedProductId
  ) {
    throw new Error(
      `${getFirmwareDisplayLabel(file)} 文件名 PID 不匹配：期望 0x${file.expectedProductId
        .toString(16)
        .toUpperCase()}，实际 0x${metadata.productId
        .toString(16)
        .toUpperCase()}`
    );
  }

  return {
    ...metadata,
    ...details,
    header: {
      hexPID: metadata.productId,
      app_version: metadata.version,
      APP2_version: null,
    },
  };
}

export async function discoverFirmwareWithFallback(
  file,
  { fetchImpl = fetch } = {}
) {
  let externalError;
  try {
    const externalFile = await discoverExternalFirmware(file, fetchImpl);
    return createFirmwareSelection(
      file,
      parseFirmwareFileName(externalFile.fileName),
      {
        url: externalFile.url,
        source: 'external',
        sourceLabel: '外部更新文件',
      }
    );
  } catch (error) {
    externalError = error;
  }

  try {
    return createFirmwareSelection(
      file,
      parseFirmwareFileName(file.bundledName),
      {
        url: file.bundledUrl,
        source: 'bundled',
        sourceLabel: '内置默认文件',
        externalError: externalError?.message || '',
      }
    );
  } catch (bundledError) {
    throw new Error(
      `${getFirmwareDisplayLabel(file)} 固件信息读取失败：外部目录 ${
        externalError?.message || '不可用'
      }；内置文件名 ${bundledError?.message || '不可用'}`
    );
  }
}

export function discoverFirmwareOnce(
  cacheOwner,
  cacheKey,
  file,
  options
) {
  if (!cacheOwner || typeof cacheOwner !== 'object') {
    return discoverFirmwareWithFallback(file, options);
  }

  let ownerCache = firmwareDiscoveryCache.get(cacheOwner);
  if (!ownerCache) {
    ownerCache = new Map();
    firmwareDiscoveryCache.set(cacheOwner, ownerCache);
  }

  if (!ownerCache.has(cacheKey)) {
    const discovery = discoverFirmwareWithFallback(file, options).catch(
      (error) => {
        ownerCache.delete(cacheKey);
        throw error;
      }
    );
    ownerCache.set(cacheKey, discovery);
  }

  return ownerCache.get(cacheKey);
}

async function readAndParseFirmware(
  url,
  fileName,
  file,
  fetchImpl,
  parser
) {
  const response = await fetchImpl(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const result = {
    ...parser.parse(await response.arrayBuffer()),
    fileName,
  };
  if (
    file.expectedProductId != null &&
    result.header?.hexPID !== file.expectedProductId
  ) {
    throw new Error(
      `PID 不匹配：期望 0x${file.expectedProductId
        .toString(16)
        .toUpperCase()}，实际 0x${Number(result.header?.hexPID)
        .toString(16)
        .toUpperCase()}`
    );
  }
  return result;
}

function assertFirmwareMatchesSelection(result, selection) {
  if (!selection || selection.version == null) return;
  const versions = [
    result.header?.app_version,
    result.header?.APP2_version,
  ];
  if (!versions.includes(selection.version)) {
    throw new Error(
      `文件名版本 0x${selection.version
        .toString(16)
        .toUpperCase()} 与 BIN 内版本不一致`
    );
  }
}

export async function loadSelectedFirmwareWithFallback(
  file,
  selection,
  {
    fetchImpl = fetch,
    parser = FirmwareBinParser,
  } = {}
) {
  let selectedError;
  try {
    const result = await readAndParseFirmware(
      selection.url,
      selection.fileName,
      file,
      fetchImpl,
      parser
    );
    assertFirmwareMatchesSelection(result, selection);
    return {
      ...result,
      source: selection.source,
      sourceLabel: selection.sourceLabel,
    };
  } catch (error) {
    selectedError = error;
  }

  if (selection.source === 'bundled') {
    throw selectedError;
  }

  try {
    const bundledSelection = createFirmwareSelection(
      file,
      parseFirmwareFileName(file.bundledName),
      {
        url: file.bundledUrl,
        source: 'bundled',
        sourceLabel: '内置默认文件',
      }
    );
    const result = await readAndParseFirmware(
      bundledSelection.url,
      bundledSelection.fileName,
      file,
      fetchImpl,
      parser
    );
    assertFirmwareMatchesSelection(result, bundledSelection);
    return {
      ...result,
      source: 'bundled',
      sourceLabel: '内置默认文件',
      externalError: selectedError?.message || '',
    };
  } catch (bundledError) {
    throw new Error(
      `${getFirmwareDisplayLabel(file)} 固件读取失败：所选文件 ${
        selectedError?.message || '不可用'
      }；内置文件 ${bundledError?.message || '不可用'}`
    );
  }
}

export async function loadFirmwareWithFallback(
  file,
  {
    fetchImpl = fetch,
    parser = FirmwareBinParser,
  } = {}
) {
  let externalError;
  try {
    let externalFile;
    try {
      externalFile = await discoverExternalFirmware(file, fetchImpl);
    } catch (discoveryError) {
      // 兼容旧的固定文件名；目录浏览未启用时仍可直接替换 mcu1.bin/mcu2.bin。
      externalFile = {
        url: file.externalUrl,
        fileName: file.externalName,
      };
    }
    return {
      ...(await readAndParseFirmware(
        externalFile.url,
        externalFile.fileName,
        file,
        fetchImpl,
        parser
      )),
      source: 'external',
      sourceLabel: '外部更新文件',
    };
  } catch (error) {
    externalError = error;
  }

  try {
    return {
      ...(await readAndParseFirmware(
        file.bundledUrl,
        file.bundledName,
        file,
        fetchImpl,
        parser
      )),
      source: 'bundled',
      sourceLabel: '内置默认文件',
      externalError: externalError?.message || '',
    };
  } catch (bundledError) {
    throw new Error(
      `${getFirmwareDisplayLabel(file)} 固件读取失败：外部文件 ${
        externalError?.message || '不可用'
      }；内置文件 ${bundledError?.message || '不可用'}`
    );
  }
}
